import React, { useState } from 'react';
import { useWeb } from '../../context/WebContext';
import { FaTrashAlt, FaEdit, FaPlus, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function ManageServices() {
  const { services, setServices } = useWeb();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    category: 'Bird Nets',
    shortDesc: '',
    longDesc: '',
    priceGuideline: '',
    image: '',
    benefitsInput: '',
  });

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const data = new FormData();
    data.append('file', file);

    const API_URL = import.meta.env.DEV ? 'https://vengamambaenterprises.com/api.php' : '/api.php';

    try {
      const res = await fetch(`${API_URL}?action=upload_image`, {
        method: 'POST',
        body: data
      });
      const result = await res.json();
      if (result.success && result.url) {
        setFormData(prev => ({
          ...prev,
          [field]: result.url
        }));
        toast.success("Image uploaded successfully!");
      } else {
        toast.error(result.error || "Failed to upload image");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Error uploading image");
    } finally {
      setUploading(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      title: '',
      category: 'Bird Nets',
      shortDesc: '',
      longDesc: '',
      priceGuideline: 'Starts from ₹15/sq.ft',
      image: '',
      benefitsInput: 'High durability nylon, Transparent/mesh view, Safe for kids & pets',
    });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = (service) => {
    setFormData({
      title: service.title,
      category: service.category || 'Bird Nets',
      shortDesc: service.shortDesc,
      longDesc: service.longDesc,
      priceGuideline: service.priceGuideline,
      image: service.image,
      benefitsInput: service.benefits.join(', '),
    });
    setEditingId(service.id);
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = () => {
    setServices(prev => prev.filter(s => s.id !== confirmDeleteId));
    toast.success('Service deleted successfully!');
    setConfirmDeleteId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (uploading) {
      toast.error('Please wait for the image upload to finish');
      return;
    }
    if (!formData.title || !formData.shortDesc || !formData.longDesc) {
      toast.error('Please fill in Title, Short Description, and Long Description');
      return;
    }

    const benefits = formData.benefitsInput
      .split(',')
      .map(b => b.trim())
      .filter(b => b.length > 0);

    if (editingId) {
      // Editing
      setServices(prev => prev.map(s => 
         s.id === editingId 
          ? { 
              ...s, 
              title: formData.title, 
              category: formData.category,
              shortDesc: formData.shortDesc, 
              longDesc: formData.longDesc, 
              priceGuideline: formData.priceGuideline, 
              image: formData.image, 
              benefits 
            } 
          : s
      ));
      toast.success('Service updated successfully!');
    } else {
      // Adding
      const newId = formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const newService = {
        id: newId,
        title: formData.title,
        category: formData.category,
        shortDesc: formData.shortDesc,
        longDesc: formData.longDesc,
        priceGuideline: formData.priceGuideline,
        image: formData.image,
        benefits,
        faqs: [
          { q: 'How long does this installation last?', a: 'Under normal weather, this setup is designed to last 5 to 7 years.' }
        ]
      };
      setServices(prev => [...prev, newService]);
      toast.success('New service added successfully!');
    }
    setModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Manage Installation Services</h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1">Add, update, or remove safety netting options available on the website.</p>
        </div>
        <button 
          onClick={openAddModal}
          className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-4 py-2.5 rounded-xl transition text-xs sm:text-sm"
        >
          <FaPlus />
          <span>Add Service</span>
        </button>
      </div>

      {/* Services Table */}
      <div className="overflow-x-auto border border-slate-200 rounded-2xl">
        <table className="w-full text-left text-xs sm:text-sm">
          <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-[10px] tracking-wider border-b">
            <tr>
              <th className="px-6 py-4">Preview</th>
              <th className="px-6 py-4">Service Name</th>
              <th className="px-6 py-4">Category</th>
              <th className="px-6 py-4">Pricing</th>
              <th className="px-6 py-4">Short Description</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-150">
            {services.map((service) => (
              <tr key={service.id} className="hover:bg-slate-50/40">
                <td className="px-6 py-4">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-12 h-12 object-cover rounded-lg border border-slate-100" 
                  />
                </td>
                <td className="px-6 py-4 font-bold text-slate-900">{service.title}</td>
                <td className="px-6 py-4 font-semibold text-slate-650">{service.category || 'Bird Nets'}</td>
                <td className="px-6 py-4 text-orange-600 font-semibold">{service.priceGuideline}</td>
                <td className="px-6 py-4 text-slate-500 truncate max-w-xs">{service.shortDesc}</td>
                <td className="px-6 py-4 text-right space-x-2.5">
                  <button 
                    onClick={() => openEditModal(service)}
                    className="text-blue-600 hover:text-blue-800 p-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                    title="Edit Service"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    onClick={() => handleDelete(service.id)}
                    className="text-red-500 hover:text-red-705 p-1.5 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    title="Delete Service"
                  >
                    <FaTrashAlt />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={() => setModalOpen(false)}></div>
          <div className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
            
            <div className="flex justify-between items-center border-b pb-4">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                {editingId ? 'Edit Installation Service' : 'Add New Service'}
              </h3>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs sm:text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Service Title *</label>
                  <input 
                    type="text" 
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                    placeholder="E.g. Balcony safety nets"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:border-orange-500 outline-none bg-white font-medium"
                    required
                  >
                    <option value="Bird Nets">Bird Nets</option>
                    <option value="Invisible Grills">Invisible Grills</option>
                    <option value="Sports Nets">Sports Nets</option>
                    <option value="Cloth Hangers">Cloth Hangers</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Pricing Guide *</label>
                  <input 
                    type="text" 
                    value={formData.priceGuideline}
                    onChange={(e) => setFormData({...formData, priceGuideline: e.target.value})}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                    placeholder="E.g. Starts from ₹15/sq.ft"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Service Image File or URL *</label>
                  <div className="flex flex-col gap-2">
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleFileUpload(e, 'image')}
                      className="w-full border border-slate-200 rounded-xl px-3 py-1.5 outline-none file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
                      disabled={uploading}
                    />
                    <div className="text-slate-400 text-center text-xs font-bold my-0.5">OR</div>
                    <input 
                      type="url" 
                      value={formData.image}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                      placeholder="Paste image URL directly..."
                      required={!formData.image}
                      disabled={uploading}
                    />
                  </div>
                  {uploading && <div className="text-xs text-orange-600 font-semibold animate-pulse">Uploading image file...</div>}
                  {formData.image && (
                    <div className="mt-2 h-16 w-24 border rounded-lg overflow-hidden bg-slate-100">
                      <img src={formData.image} alt="Preview" className="h-full w-full object-cover" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Short Card Description *</label>
                <input 
                  type="text" 
                  value={formData.shortDesc}
                  onChange={(e) => setFormData({...formData, shortDesc: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  placeholder="1-sentence snippet summarizing the net function"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Detailed Long Description *</label>
                <textarea 
                  rows="4"
                  value={formData.longDesc}
                  onChange={(e) => setFormData({...formData, longDesc: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none resize-none"
                  placeholder="Full explanation of materials, tension systems, anchoring process..."
                  required
                ></textarea>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Key Benefits (Comma Separated)</label>
                <input 
                  type="text" 
                  value={formData.benefitsInput}
                  onChange={(e) => setFormData({...formData, benefitsInput: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  placeholder="Benefit 1, Benefit 2, Benefit 3"
                />
              </div>

              <div className="pt-4 border-t flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setModalOpen(false)}
                  className="border border-slate-200 rounded-xl px-5 py-2.5 font-semibold text-slate-650 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-2.5 rounded-xl transition"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Save Service'}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={() => setConfirmDeleteId(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center space-y-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <FaTrashAlt className="text-red-600 text-lg" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Delete Service?</h3>
            <p className="text-slate-500 text-sm">This service will be permanently removed from the homepage and all dropdowns. This cannot be undone.</p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 border border-slate-200 rounded-xl py-2.5 font-semibold text-slate-700 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 rounded-xl transition"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
